import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const TreeView = ({ data, actionTemplate, deleteData }) => {
  // Get all node IDs recursively to expand all by default
  const getAllNodeIds = (nodes) => {
    const ids = new Set();
    const traverse = (nodeList) => {
      nodeList.forEach(node => {
        ids.add(node.id);
        if (node.children_recursive && node.children_recursive.length > 0) {
          traverse(node.children_recursive);
        }
      });
    };
    traverse(nodes || []);
    return ids;
  };

  const [expandedNodes, setExpandedNodes] = useState(() => getAllNodeIds(data));

  // Update expanded nodes when data changes
  useEffect(() => {
    setExpandedNodes(getAllNodeIds(data));
  }, [data]);

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const expandAll = () => {
    setExpandedNodes(getAllNodeIds(data));
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  const renderTreeNode = (node, level = 0) => {
    const hasChildren = node.children_recursive && node.children_recursive.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const indentStyle = { 
      paddingLeft: `${level * 50 + 20}px`,
      borderLeft: level > 0 ? '3px solid #dee2e6' : 'none',
      marginLeft: level > 0 ? '12px' : '0px',
      position: 'relative'
    };

    return (
      <React.Fragment key={node.id}>
        <tr className={`tree-node tree-level-${level} ${hasChildren ? 'has-children' : 'leaf-node'}`} style={{
          backgroundColor: hasChildren ? (level === 0 ? 'rgba(52, 144, 220, 0.08)' : 'rgba(52, 144, 220, 0.04)') : (level > 0 ? 'rgba(248, 249, 250, 0.5)' : 'white'),
          borderLeft: hasChildren ? (level === 0 ? '5px solid #3490dc' : '3px solid #74b9ff') : (level > 0 ? '2px solid #ddd' : 'none'),
          transition: 'all 0.2s ease',
          borderBottom: level > 0 ? '1px solid #e9ecef' : '1px solid #e9ecef',
          fontWeight: hasChildren ? (level === 0 ? '600' : '500') : '400'
        }}>
          <td className="py-3 ps-3 border-0" style={{
            ...indentStyle,
            paddingTop: level > 0 ? '16px' : '12px',
            paddingBottom: level > 0 ? '16px' : '12px'
          }}>
            {level > 0 && (
              <div className="tree-connector" style={{
                position: 'absolute',
                left: `${level * 50 - 15}px`,
                top: '50%',
                width: '25px',
                height: '2px',
                backgroundColor: '#c3d4e6',
                zIndex: 1
              }}></div>
            )}
            <div className="d-flex align-items-center" style={{position: 'relative', zIndex: 2}}>
              {hasChildren ? (
                <span
                  className={`tree-toggle me-3 ${isExpanded ? 'expanded' : 'collapsed'}`}
                  onClick={() => toggleNode(node.id)}
                  style={{ 
                    cursor: 'pointer',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    backgroundColor: level === 0 ? '#3490dc' : '#74b9ff',
                    border: 'none',
                    transition: 'all 0.2s ease',
                    color: 'white'
                  }}
                >
                  {isExpanded ? (
                    <i className="fa fa-minus" aria-hidden="true"></i>
                  ) : (
                    <i className="fa fa-plus" aria-hidden="true"></i>
                  )}
                </span>
              ) : (
                <span className="me-3" style={{
                  padding: '6px 10px',
                  borderRadius: '6px',
                  backgroundColor: 'transparent'
                }}>
                  <i className="fa fa-circle text-muted" aria-hidden="true" style={{fontSize: '6px'}}></i>
                </span>
              )}
              <span className={`account-name ${hasChildren ? 'fw-bold text-dark' : 'text-secondary'}`} style={{
                fontSize: hasChildren ? (level === 0 ? '1.1rem' : '1rem') : (level === 0 ? '1rem' : '0.95rem'),
                fontWeight: hasChildren ? (level === 0 ? '700' : '600') : (level === 0 ? '500' : '400'),
                color: hasChildren ? (level === 0 ? '#2c3e50' : '#34495e') : '#6c757d',
                letterSpacing: hasChildren ? '0.3px' : '0.1px'
              }}>
                {node.name}
              </span>
            </div>
          </td>
          <td className="border-0" style={{
            paddingTop: level > 0 ? '16px' : '12px',
            paddingBottom: level > 0 ? '16px' : '12px'
          }}>
            <span className="badge bg-light text-dark font-monospace" style={{
              fontSize: '0.75rem',
              padding: '0.4em 0.8em'
            }}>{node.code}</span>
          </td>
          <td className="border-0" style={{
            paddingTop: level > 0 ? '16px' : '12px',
            paddingBottom: level > 0 ? '16px' : '12px'
          }}>
            <span className="text-muted small">{node.type || node.name}</span>
          </td>
          <td className="border-0" style={{
            paddingTop: level > 0 ? '16px' : '12px',
            paddingBottom: level > 0 ? '16px' : '12px'
          }}>
            <span className={`fw-bold ${parseFloat(node.opening_amount || node.amount || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
              {/* {parseFloat(node.opening_amount || node.amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2})} */}
              {parseFloat(node.balance || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
            </span>
          </td>
          <td className="border-0" style={{
            paddingTop: level > 0 ? '16px' : '12px',
            paddingBottom: level > 0 ? '16px' : '12px'
          }}>
            <span className="text-muted small">{node.description || '-'}</span>
          </td>
          <td className="border-0 text-center" style={{
            paddingTop: level > 0 ? '16px' : '12px',
            paddingBottom: level > 0 ? '16px' : '12px'
          }}>
            {actionTemplate ? (
              actionTemplate(node, deleteData)
            ) : (
              <Link
                to={{
                  pathname: "/edit-chart-of-account/" + node.id,
                }}
                className="btn btn-outline-primary btn-sm rounded-pill"
                title="Edit Account"
                style={{
                  borderWidth: '1.5px',
                  transition: 'all 0.2s ease'
                }}
              >
                <i className="fa fa-edit" aria-hidden="true"></i>
              </Link>
            )}
          </td>
        </tr>
        {hasChildren && isExpanded &&
        node.children_recursive.map((child) => renderTreeNode(child, level + 1))
        }
      </React.Fragment>
    );
  };

  return (
    <div className="tree-view-container" style={{
      background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
    }}>
      <div className="tree-controls mb-4 d-flex justify-content-between align-items-center" style={{
        background: 'white',
        padding: '16px 20px',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <div className="control-buttons" style={{
          display: 'flex',
          gap: '8px'
        }}>
          <button 
            className="btn btn-outline-primary btn-sm shadow-sm" 
            onClick={expandAll}
            disabled={expandedNodes.size === getAllNodeIds(data).size}
            style={{
              borderRadius: '6px',
              fontWeight: '500',
              transition: 'all 0.2s ease',
              borderWidth: '1.5px'
            }}
          >
            <i className="fa fa-expand me-2"></i>
            Expand All
          </button>
          <button 
            className="btn btn-outline-secondary btn-sm shadow-sm" 
            onClick={collapseAll}
            disabled={expandedNodes.size === 0}
            style={{
              borderRadius: '6px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
          >
            <i className="fa fa-compress me-2"></i>
            Collapse All
          </button>
        </div>
        <div className="tree-info text-muted small" style={{
          fontSize: '0.85rem',
          padding: '8px 12px',
          background: 'rgba(108, 117, 125, 0.1)',
          borderRadius: '20px'
        }}>
          <i className="fa fa-info-circle me-1"></i>
          {data ? data.length : 0} root accounts
        </div>
      </div>
      <div className="table-responsive shadow-sm rounded" style={{
        background: 'white',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <table className="table table-hover mb-0 modern-table">
          <thead className="table-header" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'black'
          }}>
            <tr>
              <th className="border-0 text-uppercase fw-bold py-3 ps-3" style={{
                color: 'black',
                fontSize: '0.85rem',
                letterSpacing: '0.5px'
              }}>
                <i className="fa fa-sitemap me-2"></i>Account Name
              </th>
              <th className="border-0 text-uppercase fw-bold py-3" style={{
                color: 'black',
                fontSize: '0.85rem',
                letterSpacing: '0.5px'
              }}>
                <i className="fa fa-code me-2"></i>Code
              </th>
              <th className="border-0 text-uppercase fw-bold py-3" style={{
                 color: 'black',
                 fontSize: '0.85rem',
                 letterSpacing: '0.5px'
               }}>
                 <i className="fa fa-tag me-2"></i>Type
               </th>
               <th className="border-0 text-uppercase fw-bold py-3" style={{
                 color: 'black',
                 fontSize: '0.85rem',
                 letterSpacing: '0.5px'
               }}>
                 <i className="me-2">৳</i>Amount
               </th>
               <th className="border-0 text-uppercase fw-bold py-3" style={{
                 color: 'black',
                 fontSize: '0.85rem',
                 letterSpacing: '0.5px'
               }}>
                 <i className="fa fa-file-text me-2"></i>Description
               </th>
               <th className="border-0 text-uppercase fw-bold py-3 text-center" style={{
                 color: 'black',
                 fontSize: '0.85rem',
                 letterSpacing: '0.5px'
               }}>
                 <i className="fa fa-cogs me-2"></i>Actions
               </th>
            </tr>
          </thead>
        <tbody>
          {data && data.map((node) => renderTreeNode(node))}
        </tbody>
      </table>

      </div>
    </div>
  );
};

export default TreeView;